<script>
import { format, parseISO } from "date-fns";
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableFilterDropdown from "./TableFilterDropdown.vue";
import ToggleInfoModal from "./ToggleInfoModal.vue";

export default {
  name: "GSheetTable",
  components: {
    TableFilterDropdown,
    ToggleInfoModal
  },
  props: {
    rows: {
      type: Object,
      required: true
    }
  },
  mounted(){
    console.log(this.rows)
  },
  data() {
    return {
      togglingRows: new Set(),
      openDropdown: null,
      isInfoOpen: false,
    };
  },
  computed: {
    tableHeaders() {
      if (!this.rows.items || this.rows.items.length === 0) return [];

      const dynamicColumns = this.rows.schema.columns.map((column) => column);

      return [{
        name: "TOGGLE",
        final_type: "boolean",
        supports: ["status"],
      }, ...dynamicColumns];
    }
  },
  methods: {
    ...mapActions('gsheet', ['toggleGSheetRow']),
    openInfo() {
      this.isInfoOpen = true;
    },
    toggleDropdown(header) {
      this.openDropdown = this.openDropdown === header ? null : header;
    },
    handleColumnAction({ column, action, value }) {
      this.$emit('action', {
        column: column,
        action: action,
        value: value
      })
      this.openDropdown = null;
    },
    changePage(page) {
      this.$emit("page-change", page);
    },
    async handleToggleRow(item) {
      if (this.togglingRows.has(item.id)) return;

      try {
        this.togglingRows.add(item.id);

        item.is_enabled = !item.is_enabled;

        await this.toggleGSheetRow(item.id);
        this.$emit('row-updated');
        useAlert("Row toggled");
      } catch (error) {
        console.error('Failed to toggle row:', error);

        item.is_enabled = !item.is_enabled;

        useAlert("Failed to toggle");
      } finally {
        this.togglingRows.delete(item.id);
      }
    },
    changePageSize(event) {
      this.$emit("page-size-change", parseInt(event.target.value, 10));
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        return format(parseISO(dateStr), "dd MMM yyyy, hh:mm a");
      } catch (e) {
        return dateStr;
      }
    },
    formatValue(key, item) {
      if (key === "id") return item.id;
      if (key === "is_enabled") return item.is_enabled ? "Yes" : "No";
      if (key === "created_at" || key === "updated_at") return this.formatDate(item[key]);
      return item[key] ?? "-";
    }
  }
};
</script>

<template>
  <div class="p-4">
    <!-- Modals -->
    <ToggleInfoModal v-if="isInfoOpen" @close="isInfoOpen = false" />

    <!-- Table Wrapper with Horizontal Scroll -->
    <div class="overflow-x-auto">
      <table class="min-w-max border border-slate-200 dark:border-slate-700 text-sm rounded-lg">
        <thead class="bg-woot-500 dark:bg-woot-800">
          <tr>
            <th
              v-for="column in tableHeaders"
              :key="column.name"
              class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 font-semibold text-white whitespace-nowrap text-left uppercase text-xs tracking-wider relative"
            >
              <div class="flex items-center space-x-2">
                <!-- Header text -->
                <span v-if="column.name === 'TOGGLE'">
                  {{ column.name.replace(/_/g, " ") }}
                  <woot-button
                    icon="info"
                    variant="clear"
                    color-scheme="secondary"
                    @click="openInfo"
                    class="rounded-full"
                  />
                </span>
                <span v-else>{{ column.name.replace(/_/g, " ") }}</span>

                <TableFilterDropdown
                  v-if="column.name !== 'TOGGLE'"
                  :column="column"
                  :isOpen="openDropdown === column.name"
                  @toggle="toggleDropdown(column.name)"
                  @close="openDropdown = null"
                  @action="handleColumnAction"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in rows.items"
            :key="item.id"
            class="transition-colors duration-200 ease-in-out hover:bg-slate-50 dark:hover:bg-slate-700"
            :class="index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/50 dark:bg-slate-700/50'"
            :title="`Last updated: ${formatDate(item.updated_at)}`"
          >
            <td
              v-for="column in tableHeaders"
              :key="column.name"
              class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
              :class="[column.name === 'id' ? 'font-medium text-slate-700 dark:text-slate-200' : 'text-slate-600 dark:text-slate-300']"
            >
              <template v-if="column.name === 'TOGGLE'">
                <label 
                  class="inline-flex items-center" 
                  :class="{ 'cursor-pointer': !togglingRows.has(item.id), 'cursor-not-allowed': togglingRows.has(item.id) }"
                  :title="togglingRows.has(item.id) ? 'Updating...' : (item.is_enabled ? 'Click to disable' : 'Click to enable')"
                >
                  <input
                    type="checkbox"
                    class="sr-only peer"
                    :checked="item.is_enabled"
                    @change="handleToggleRow(item)"
                    :disabled="togglingRows.has(item.id)"
                  />
                  <div
                    class="w-10 h-5 rounded-full relative transition-all duration-300 ease-in-out"
                    :class="{
                      'bg-green-500': item.is_enabled && !togglingRows.has(item.id),
                      'bg-slate-300 dark:bg-slate-600': !item.is_enabled && !togglingRows.has(item.id),
                      'bg-slate-200 dark:bg-slate-500': togglingRows.has(item.id),
                      'opacity-50': togglingRows.has(item.id)
                    }"
                  >
                    <div
                      class="absolute w-4 h-4 bg-white dark:bg-slate-300 rounded-full shadow-sm transition-all duration-300 ease-in-out"
                      :class="{
                        'left-0.5 top-0.5': !item.is_enabled,
                        'left-[1.375rem] top-0.5': item.is_enabled,
                        'animate-pulse': togglingRows.has(item.id)
                      }"
                    ></div>
                  </div>
                </label>
              </template>
              <template v-else>
                {{ formatValue(column.name, item) }}
              </template>
            </td>
          </tr>
          <tr v-if="rows.items.length === 0">
            <td
              :colspan="tableHeaders.length"
              class="text-center py-8 text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50"
            >
              No records found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination & Page Size -->
    <div class="flex justify-between items-center mt-4">
      <!-- Page Size -->
      <div class="flex items-center">
        <label for="pageSize" class="text-sm text-slate-600 dark:text-slate-300 mr-3">Rows per page:</label>
        <select
          id="pageSize"
          class="block mb-0 w-20 rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 py-2 pl-3 pr-8 text-sm focus:border-slate-500 focus:ring-slate-500 focus:outline-none"
          :value="rows.limit"
          @change="changePageSize"
        >
          <option v-for="size in [10,20,50,100]" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
      </div>

      <!-- Pagination -->
      <div class="flex items-center space-x-4">
        <woot-button 
          icon="chevron-left"
          color-scheme="secondary"
          variant="clear"
          class="hover:bg-slate-50 dark:hover:bg-slate-700"
          :disabled="!rows.has_prev"
          @click="changePage(rows.page - 1)"
        />
        <span class="text-sm text-slate-600 dark:text-slate-300">
          Page <span class="font-medium text-slate-900 dark:text-slate-100">{{ rows.page }}</span> of
          <span class="font-medium text-slate-900 dark:text-slate-100">{{ rows.pages }}</span>
        </span>
        <woot-button 
          icon="chevron-right"
          color-scheme="secondary"
          variant="clear"
          class="hover:bg-slate-50 dark:hover:bg-slate-700"
          :disabled="!rows.has_next"
          @click="changePage(rows.page + 1)"
        />
      </div>
    </div>
  </div>
</template>

